// The alert card inside the right-hand info panel.
//
// Shows only the most recent alert for this screen's city. A newer alert
// cross-fades in and takes its place; once the newest one is more than 24 hours
// old this renders the empty state instead (see useLatestAlert).
//
// It lives inside InfoPanel's own glass card, so it is styled as an inset
// sub-card — matching the High/Low and details boxes in the weather panel on
// the left — rather than carrying a second blurred background of its own.
// Because it sits in a flex column with the full height of the panel to grow
// into, its height no longer has to be clamped to avoid the carousel.

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FONT_STACK = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const SEVERITY_COLORS = {
  High: '#ef4444',
  Medium: '#f59e0b',
  Low: '#3b82f6'
};

// Long alert bodies still get a ceiling so one verbose advisory can't push the
// station footer off the bottom of the panel.
const clampLines = (lines) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
});

const CARD_BASE = {
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 12,
  padding: '18px 20px',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
  fontFamily: FONT_STACK
};

function relativeTime(createdAt, now) {
  const timestamp = Date.parse(createdAt);
  if (!Number.isFinite(timestamp)) return null;

  const minutes = Math.max(0, Math.round((now - timestamp) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
}

export const LiveAlertPanel = ({ alert }) => {
  // The alert object only changes when a new alert arrives, so the "x min ago"
  // label needs its own tick to stay honest.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const accent = alert
    ? (SEVERITY_COLORS[alert.severity] || alert.color || '#3b82f6')
    : '#3b82f6';
  const age = alert ? relativeTime(alert.createdAt, now) : null;

  return (
    <AnimatePresence mode="wait">
      {alert ? (
        <motion.div
          key={alert.id}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6, filter: 'blur(4px)' }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          style={{
            ...CARD_BASE,
            borderLeft: `3px solid ${accent}`
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 10
          }}>
            <motion.span
              animate={{ opacity: [1, 0.25, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                flexShrink: 0,
                background: accent,
                boxShadow: `0 0 8px ${accent}`
              }}
            />
            <span style={{
              color: accent,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase'
            }}>
              {alert.severity || 'Active'}
            </span>
          </div>

          <p style={{
            color: '#ffffff',
            fontSize: 17,
            fontWeight: 600,
            lineHeight: 1.35,
            margin: 0,
            ...clampLines(3)
          }}>
            {alert.title}
          </p>

          {alert.text && (
            <p style={{
              color: 'rgba(255,255,255,0.72)',
              fontSize: 13.5,
              lineHeight: 1.55,
              margin: '8px 0 0 0',
              ...clampLines(6)
            }}>
              {alert.text}
            </p>
          )}

          <p style={{
            color: 'rgba(255,255,255,0.38)',
            fontSize: 11.5,
            letterSpacing: 0.3,
            margin: '12px 0 0 0'
          }}>
            {[alert.city, age].filter(Boolean).join(' · ')}
          </p>
        </motion.div>
      ) : (
        // Keeps the panel looking deliberate on the (common) days with nothing
        // to report, rather than leaving a gap where a card used to be.
        <motion.div
          key="no-alert"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            ...CARD_BASE,
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <span style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            flexShrink: 0,
            background: '#22c55e',
            boxShadow: '0 0 8px rgba(34,197,94,0.7)'
          }} />
          <p style={{
            color: 'rgba(255,255,255,0.55)',
            fontSize: 13.5,
            lineHeight: 1.5,
            margin: 0
          }}>
            No weather alerts in the last 24 hours
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
